/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription, forkJoin } from 'rxjs';
import { IUser } from 'src/app/features/models/iuser.modal';
import { messagesConfig } from 'src/app/shared/constants/message.constants';
import { WorksheetService } from 'src/app/features/worksheet/services/worksheet.service';
import { environment } from 'src/environments/environment';
import { UserService } from 'src/app/shared/services/user.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'app-assign-to-labtech',
    templateUrl: './assign-to-labtech.component.html',
    styleUrls: ['./assign-to-labtech.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AssignToLabtechComponent implements OnInit, OnDestroy {
  public config: any;
  public additionalNotes: string | undefined;
  public assignToLabtechForm!: FormGroup;
  public userDropDown: IUser[] = [];
  public assignToLabtechSubscription = Subscription.EMPTY;
  public assignToAllWorksheet = false;
  public assignTypeProject = false;

  public multipleSelected = false;
  public totalNumOfWorksheets = 0;
  userRoles: any;

  constructor(
    public matDialogRef: MatDialogRef<AssignToLabtechComponent>,
    @Inject(MAT_DIALOG_DATA) data: MatDialog,
    public userService: UserService,
    private formBuilder: FormBuilder,
    public worksheetService: WorksheetService,
    private snackBar: MatSnackBar,
  ) {
    this.config = data;
    this.multipleSelected = this.config.multipleSelected;
    this.totalNumOfWorksheets = this.config.totalNumOfWorksheets;
    this.assignTypeProject = 'worksheetId' in this.config;
    this.userRoles = this.config.role;
  }

  ngOnInit(): void {
    this.fetchLabTechs();
    this.assignToLabtechForm = this.formBuilder.group({
      userId: ['', [Validators.required]],
      assignAll: [false],
      role: [''],
      additionalNotes: [null],
    });
  }

  assignToLabtech(): void {
    if (!this.assignToLabtechForm.valid) return;

    const formValue = this.assignToLabtechForm.value;
    const selectedUser = this.userDropDown.find((user) => user.id === formValue.userId);
    if (!selectedUser) return;

    const commonPayload = {
      aadObjectId: selectedUser.id,
      firstName: selectedUser.givenName,
      lastName: selectedUser.surname,
      userName: selectedUser.displayName,
      email: selectedUser.userPrincipalName,
      role: selectedUser.role,
      additionalNotes: formValue.additionalNotes || null,
      source: this.config?.source ?? null,
    };

    if (this.multipleSelected) {
      this.handleMultipleAssignment(commonPayload);
    } else if ('worksheetId' in this.config) {
      this.handleSingleAssignment(commonPayload, formValue.assignAll);
    } else {
      this.handleProjectAssignment(commonPayload);
    }
  }

  private handleMultipleAssignment(commonPayload: any): void {
    const payload = {
      ...commonPayload,
      worksheetRowIds: this.config.worksheetRows.map((w: any) => w.id),
    };

    this.assignToLabtechSubscription = this.worksheetService
      .assignMultipleToLabtech(payload, this.config?.type)
      .subscribe({
        next: () => this.showSuccessMessage(messagesConfig.assignTolabTech.message, true),
      });
  }

  private handleSingleAssignment(commonPayload: any, assignAll: boolean): void {
    const payload = {
      ...commonPayload,
      worksheetRowId: this.config.worksheetId.id,
      assignAll,
    };

    this.assignToLabtechSubscription = this.worksheetService
      .assignSingleToLabtech(payload, this.config?.type)
      .subscribe({
        next: () => this.showSuccessMessage(messagesConfig.assignTolabTech.message, true),
      });
  }

  private handleProjectAssignment(commonPayload: any): void {
    const payload = {
      ...commonPayload,
      projectId: this.config.projectId,
    };

    this.assignToLabtechSubscription = this.worksheetService
      .assignProject(payload, this.config?.type)
      .subscribe({
        next: (userDetails: any) => this.showSuccessMessage(messagesConfig.changeSuccessMeaage.message, userDetails),
      });
  }

  private showSuccessMessage(message: string, result: any): void {
    this.snackBar.open(message, 'DONE', {
      duration: 3000,
      verticalPosition: 'top',
      panelClass: ['default-snackbar'],
    });
    this.closeDialog(result);
  }

  closeDialog(assigned: boolean): void {
    this.matDialogRef.close(assigned);
  }

  roleCheck(roles: string[]): boolean {
    return roles.some((role) => this.userRoles.includes(role));
  }

  fetchLabTechs(): void {
    // Each user's role comes from the group they are listed in
    const groupsToFetch: { groupId: string; role: string }[] = [];
    if ('worksheetId' in this.config) {
      if (this.roleCheck(['Manager', 'Admin'])) {
        groupsToFetch.push(
          { groupId: environment.labTechGroupId, role: 'Labtech' },
          { groupId: environment.labManagerGroupId, role: 'Manager' }
        );
      } else if (this.roleCheck(['Labtech'])) {
        groupsToFetch.push({ groupId: environment.labManagerGroupId, role: 'Manager' });
      }
    } else {
      groupsToFetch.push(
        { groupId: environment.reportWriterGroupId, role: 'Reportwriter' },
        { groupId: environment.adminGroupId, role: 'Admin' }
      );
    }

    if (groupsToFetch.length === 0) {
      this.userDropDown = [];
      return;
    }

    forkJoin(groupsToFetch.map((g) => this.userService.getUsersByGroupIdGraphApi(g.groupId))).subscribe({
      next: (groups) => {
        this.userDropDown = groups.flatMap((group: any, i: number) =>
          group.value.map((user: IUser) => ({
            ...user,
            role: groupsToFetch[i].role
          }))
        );
      },
      error: (err: any) => console.error('Error fetching users:', err)
    });
  }

  onInputChange(event: any): void {
    let currentValue = event.target.value;
    if (currentValue.startsWith(' ')) {
      event.preventDefault();
      this.assignToLabtechForm.get('additionalNotes')?.setValue(currentValue.slice(1));
      return;
    }
    currentValue = currentValue.replace(/[^A-Za-z0-9-,.;'&/.() @#$%^*!?_+=|~`{}[\]<>:;\\ ]+/g, ' ');
    if (currentValue.trim() === '') {
      this.assignToLabtechForm.get('additionalNotes')?.setValue('');
    } else {
      this.assignToLabtechForm.get('additionalNotes')?.setValue(currentValue);
    }
  }

  ngOnDestroy() {
    this.assignToLabtechSubscription.unsubscribe();
  }
}
